import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { useNavigation } from '../contexts/NavigationContext';

const useKeyboardNavigation = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { goBack, quickAccessItems } = useNavigation();

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle keyboard shortcuts when not typing in input fields
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.contentEditable === 'true'
      ) {
        return;
      }

      // Handle keyboard shortcuts
      if (event.altKey) {
        switch (event.key.toLowerCase()) {
          case 'h':
            event.preventDefault();
            // Navigate to home/dashboard
            if (user?.role === 'va') {
              navigate('/va-dashboard');
            } else if (user?.role === 'employer') {
              navigate('/employer-dashboard');
            }
            break;

          case 'n': {
            event.preventDefault();
            // Focus on navigation (could trigger sidebar or search)
            const searchInput = document.querySelector('[data-search-input]');
            if (searchInput) {
              searchInput.focus();
            }
            break;
          }

          case 's':
            event.preventDefault();
            // Navigate to search
            if (user?.role === 'va') {
              navigate('/job-search');
            } else if (user?.role === 'employer') {
              navigate('/candidate-search');
            }
            break;

          case 'm':
            event.preventDefault();
            // Navigate to messages
            if (user?.role === 'va') {
              navigate('/messages-communication');
            } else if (user?.role === 'employer') {
              navigate('/messages');
            }
            break;

          case 'p':
            event.preventDefault();
            // Navigate to profile
            if (user?.role === 'va') {
              navigate('/va-profile');
            } else if (user?.role === 'employer') {
              navigate('/employer-profile-settings');
            }
            break;

          case 'a':
            event.preventDefault();
            // Navigate to analytics
            if (user?.role === 'va') {
              navigate('/analytics');
            } else if (user?.role === 'employer') {
              navigate('/advanced-analytics');
            }
            break;

          case 'j':
            event.preventDefault();
            // Navigate to jobs
            if (user?.role === 'va') {
              navigate('/job-search');
            } else if (user?.role === 'employer') {
              navigate('/job-posting');
            }
            break;

          default: {
            // Handle number keys for quick access items
            const num = parseInt(event.key);
            if (num >= 1 && num <= 9) {
              event.preventDefault();
              const quickItem = quickAccessItems[num - 1];
              if (quickItem) {
                navigate(quickItem.path);
              }
            }
            break;
          }
        }
      }

      // Handle other navigation shortcuts
      switch (event.key) {
        case 'Escape': {
          // Close any open modals or dropdowns
          const closeButtons = document.querySelectorAll('[data-close-on-escape]');
          closeButtons.forEach(button => button.click());
          break;
        }

        case 'Backspace':
          if (event.altKey) {
            event.preventDefault();
            goBack();
          }
          break;

        case '/':
          if (!event.ctrlKey && !event.altKey) {
            event.preventDefault();
            // Focus on search input
            const searchInput = document.querySelector('[data-search-input]');
            if (searchInput) {
              searchInput.focus();
            }
          }
          break;

        case '?':
          if (event.shiftKey) {
            event.preventDefault();
            // Show keyboard shortcuts help
            showKeyboardShortcuts();
          }
          break;

        default:
          break;
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate, user, goBack, quickAccessItems]);

  const showKeyboardShortcuts = () => {
    // Create and show a modal with keyboard shortcuts
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    modal.setAttribute('data-close-on-escape', 'true');
    
    const shortcuts = [
      { key: 'Alt + H', description: 'Go to Dashboard' },
      { key: 'Alt + S', description: 'Go to Search' },
      { key: 'Alt + M', description: 'Go to Messages' },
      { key: 'Alt + P', description: 'Go to Profile' },
      { key: 'Alt + A', description: 'Go to Analytics' },
      { key: 'Alt + J', description: 'Go to Jobs' },
      { key: 'Alt + 1-9', description: 'Quick Access Items' },
      { key: 'Alt + Backspace', description: 'Go Back' },
      { key: '/', description: 'Focus Search' },
      { key: 'Escape', description: 'Close Modals' },
      { key: '?', description: 'Show This Help' }
    ];

    modal.innerHTML = `
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-900">Keyboard Shortcuts</h3>
          <button onclick="this.closest('[data-close-on-escape]').remove()" class="text-gray-400 hover:text-gray-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div class="space-y-2">
          ${shortcuts.map(shortcut => `
            <div class="flex items-center justify-between py-1">
              <span class="text-sm text-gray-600">${shortcut.description}</span>
              <kbd class="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">${shortcut.key}</kbd>
            </div>
          `).join('')}
        </div>
        <div class="mt-4 pt-4 border-t border-gray-200">
          <p class="text-xs text-gray-500">
            Press <kbd class="px-1 py-0.5 text-xs bg-gray-100 border rounded">Escape</kbd> to close this dialog
          </p>
        </div>
      </div>
    `;

    // Close on click outside
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });

    document.body.appendChild(modal);

    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (document.body.contains(modal)) {
        modal.remove();
      }
    }, 10000);
  };

  return {
    showKeyboardShortcuts
  };
};

export default useKeyboardNavigation;
